// Scoped customer loader for the design-approved review screens.
//
// Prior to W1B the CustomerReviewBoundary hard-coded the SARAH fixture id as
// the only allowed customer. That shipped by design in R1 so the fixture
// pack couldn't leak under arbitrary route ids, but it is NOT production
// authorization. This module replaces that allowlist with a scoped loader:
//
//   - In production the hook reads the current HubSpot session, calls the
//     signed api-server at `GET /v1/contacts/:id` with the session JWT, and
//     relies on the server's rep-scope check to return 200/403/404.
//
//   - In dev / design review the explicit opt-in flag `?preview=sarah` on
//     the route (or a parent providing `<PreviewFixtureProvider />`) renders
//     the SARAH fixture synchronously. Opt-in is explicit so a real session
//     never falls back to sample data.
//
//   - A dev mock session (see `auth/mockContext`) also unlocks the SARAH
//     fixture because that session's token is already recognised as the
//     local dev bearer and the api-server does not accept it in production.
//
//   - Tests and the design-review harness can inject their own loader via
//     `<CustomerLoaderProvider loader={...} />`. The loader is a React hook,
//     so a test fixture resolves synchronously and no `waitFor` is needed.
//
// The returned state is a small discriminated union consumed by
// CustomerReviewBoundary. The customer payload itself is intentionally
// minimal: the review screens still consume their own data boundary; this
// module only decides whether the rep is allowed to see this customer at
// this route and gives the chrome enough to render a safe fallback.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { isDevMockSession, SAMPLE_CONTACT_ID } from '../../auth/mockContext.js';
import { useOptionalSession } from '../../auth/session-context.js';
import type { HubSpotSession } from '../../auth/hubspot-handshake.js';
import { MICHAEL, SARAH } from '../../data/mockData.js';

export interface AuthorizedCustomer {
  id: string;
  initials: string;
  firstName: string;
  fullName: string;
  email: string;
}

export type CustomerLoadResult =
  | { status: 'loading' }
  | { status: 'ready'; customer: AuthorizedCustomer }
  | { status: 'unauthorized' }
  | { status: 'not-found' }
  | { status: 'error'; error: Error };

export type CustomerLoaderHook = (
  customerId: string | null,
) => CustomerLoadResult;

const CustomerLoaderContext = createContext<CustomerLoaderHook | null>(null);

export interface CustomerLoaderProviderProps {
  loader: CustomerLoaderHook;
  children: ReactNode;
}

/**
 * Inject a replacement loader into the subtree. The runtime default (an
 * authenticated api-server fetch) is used when no provider is mounted.
 * Tests and the design-review harness provide their own hook so they can
 * resolve synchronously.
 */
export function CustomerLoaderProvider({
  loader,
  children,
}: CustomerLoaderProviderProps): ReactNode {
  // Freeze loader identity across renders so React sees the same hook
  // sequence on every render.
  const ref = useRef(loader);
  ref.current = loader;
  const stable = useMemo<CustomerLoaderHook>(
    () => (customerId) => ref.current(customerId),
    [],
  );
  return (
    <CustomerLoaderContext.Provider value={stable}>
      {children}
    </CustomerLoaderContext.Provider>
  );
}

// --- Preview fixture context ---------------------------------------------

const PreviewFixtureContext = createContext<boolean>(false);

export interface PreviewFixtureProviderProps {
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Explicit opt-in to the design-review fixture pack. Wrap a route subtree
 * when a build or test wants the SARAH / MICHAEL fixtures without the
 * `?preview=sarah` query flag. Never mount this at the production root.
 */
export function PreviewFixtureProvider({
  enabled = true,
  children,
}: PreviewFixtureProviderProps): ReactNode {
  return (
    <PreviewFixtureContext.Provider value={enabled}>
      {children}
    </PreviewFixtureContext.Provider>
  );
}

export function usePreviewFixtureMode(): boolean {
  const ctx = useContext(PreviewFixtureContext);
  const [search] = useSearchParams();
  const flag = search.get('preview');
  return ctx || flag === 'sarah' || flag === 'michael';
}

// --- Default production loader ------------------------------------------

interface FetchDeps {
  fetchImpl: typeof fetch;
  baseUrl: string;
  getToken: () => string | null;
}

function readRuntimeBaseUrl(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as { __RUNTIME_API_BASE_URL__?: string }).__RUNTIME_API_BASE_URL__
      : undefined;
  if (typeof injected === 'string' && injected.length > 0) return injected;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const fromEnv = env?.VITE_RUNTIME_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return '';
}

function toAuthorizedCustomer(payload: Record<string, unknown>): AuthorizedCustomer | null {
  // W27 (2026-10-20): the W25 api-server route answers
  //   { result: 'OK', contactId, profile: { id, firstName, lastName, ... } }
  // so the record lives under `profile`. Reading `payload.id` (the pre-W25
  // shape) returned null for every real contact, which made this loader
  // report 'not-found' on any signed session and flashed "This customer's
  // review is not available" between Finish & Next records on url-bootstrap
  // sessions. Accept both shapes.
  const nested = payload['profile'];
  const src: Record<string, unknown> =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : payload;
  const id =
    typeof src['id'] === 'string'
      ? (src['id'] as string)
      : typeof payload['contactId'] === 'string'
        ? (payload['contactId'] as string)
        : null;
  if (!id) return null;
  const firstName =
    typeof src['firstName'] === 'string'
      ? (src['firstName'] as string)
      : '';
  const lastName =
    typeof src['lastName'] === 'string'
      ? (src['lastName'] as string)
      : '';
  const email =
    typeof src['email'] === 'string' ? (src['email'] as string) : '';
  const fullName =
    typeof src['fullName'] === 'string'
      ? (src['fullName'] as string)
      : [firstName, lastName].filter(Boolean).join(' ').trim();
  const initials =
    typeof src['initials'] === 'string'
      ? (src['initials'] as string)
      : [firstName.charAt(0), lastName.charAt(0)].join('').toUpperCase() || id.slice(0, 2).toUpperCase();
  return {
    id,
    initials,
    firstName,
    fullName: fullName || id,
    email,
  };
}

async function fetchCustomer(
  deps: FetchDeps,
  customerId: string,
  session: HubSpotSession | null,
): Promise<CustomerLoadResult> {
  const token = deps.getToken();
  if (!token || !session) return { status: 'unauthorized' };
  const base = deps.baseUrl.endsWith('/') ? deps.baseUrl.slice(0, -1) : deps.baseUrl;
  const url = `${base}/v1/contacts/${encodeURIComponent(customerId)}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    'X-Customer-Id': customerId,
    'X-Portal-Id': String(session.portalId),
  };
  // W27: same identity headers the other same-origin reads send on a
  // url-bootstrap session (see useContactProfile.buildSessionHeaders), so
  // the api-server attributes this read to the rep instead of the
  // 'url-bootstrap-user' placeholder.
  if (session.urlBootstrapped) {
    if (session.userId) headers['X-User-Id'] = session.userId;
    if (session.email) headers['X-User-Email'] = session.email;
  }
  let response: Response;
  try {
    response = await deps.fetchImpl(url, {
      method: 'GET',
      headers,
    });
  } catch (err) {
    return {
      status: 'error',
      error:
        err instanceof Error
          ? err
          : new Error('Network error while loading customer.'),
    };
  }
  if (response.status === 401) return { status: 'unauthorized' };
  if (response.status === 403) return { status: 'unauthorized' };
  if (response.status === 404) return { status: 'not-found' };
  if (!response.ok) {
    return {
      status: 'error',
      error: new Error(`Customer load failed with status ${response.status}.`),
    };
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch (err) {
    return {
      status: 'error',
      error:
        err instanceof Error
          ? err
          : new Error('Customer response was not valid JSON.'),
    };
  }
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { status: 'not-found' };
  }
  const customer = toAuthorizedCustomer(body as Record<string, unknown>);
  if (!customer) return { status: 'not-found' };
  return { status: 'ready', customer };
}

function useDefaultCustomerLoader(
  customerId: string | null,
): CustomerLoadResult {
  const sessionCtx = useOptionalSession();
  const [state, setState] = useState<CustomerLoadResult>({ status: 'loading' });
  // Pin the fetch dependencies to the mount; swapping fetch across renders
  // would cancel the in-flight request without updating state.
  const depsRef = useRef<FetchDeps | null>(null);
  if (!depsRef.current) {
    depsRef.current = {
      fetchImpl: typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null!,
      baseUrl: readRuntimeBaseUrl(),
      getToken: () => sessionCtx?.session?.token ?? null,
    };
  } else {
    // Keep the token accessor fresh so a refresh picks up the new token.
    depsRef.current.getToken = () => sessionCtx?.session?.token ?? null;
  }

  useEffect(() => {
    if (!customerId) {
      setState({ status: 'not-found' });
      return;
    }
    if (!sessionCtx || sessionCtx.status === 'pending') {
      setState({ status: 'loading' });
      return;
    }
    if (sessionCtx.status === 'error' || sessionCtx.status === 'expired') {
      setState({ status: 'unauthorized' });
      return;
    }
    const session = sessionCtx.session;
    if (!session) {
      setState({ status: 'unauthorized' });
      return;
    }
    if (!depsRef.current || !depsRef.current.fetchImpl) {
      setState({
        status: 'error',
        error: new Error('No fetch implementation is available in this environment.'),
      });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    void fetchCustomer(depsRef.current, customerId, session).then((result) => {
      if (!cancelled) setState(result);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, sessionCtx]);

  return state;
}

// --- Dev / preview fixture loader ---------------------------------------

const FIXTURE_CUSTOMERS: Record<string, AuthorizedCustomer> = {
  [SARAH.id]: {
    id: SARAH.id,
    initials: SARAH.initials,
    firstName: SARAH.firstName,
    fullName: SARAH.fullName,
    email: SARAH.email,
  },
  // The mock session's objectId uses the sample- prefix. Alias it to SARAH
  // so a root redirect from the dev mock lands on a usable fixture record.
  [SAMPLE_CONTACT_ID]: {
    id: SARAH.id,
    initials: SARAH.initials,
    firstName: SARAH.firstName,
    fullName: SARAH.fullName,
    email: SARAH.email,
  },
  [MICHAEL.id]: {
    id: MICHAEL.id,
    initials: MICHAEL.initials,
    firstName: MICHAEL.firstName,
    fullName: MICHAEL.fullName,
    email: MICHAEL.email,
  },
};

function loadFixtureCustomer(customerId: string | null): CustomerLoadResult {
  if (!customerId) return { status: 'not-found' };
  const match = FIXTURE_CUSTOMERS[customerId];
  if (!match) return { status: 'not-found' };
  return { status: 'ready', customer: match };
}

// --- Public hook --------------------------------------------------------

/**
 * Resolve the customer record for the current route against the rep's
 * scope. Returns a discriminated state that the boundary renders through
 * a loading / unauthorized / not-found / error / ready switch.
 *
 * The loader hook is picked once per subtree. Mounting a
 * CustomerLoaderProvider mid-session is not supported (would reorder the
 * inner hook calls), and the test harness mounts the provider at the
 * root of the tree it renders.
 */
export function useAuthenticatedCustomer(
  customerId: string | null,
): CustomerLoadResult {
  const injectedLoader = useContext(CustomerLoaderContext);
  const previewMode = usePreviewFixtureMode();
  const session = useOptionalSession();
  const devMock = isDevMockSession(session?.session ?? null);
  const loader: CustomerLoaderHook = injectedLoader ?? useDefaultCustomerLoader;
  const loaderResult = loader(customerId);

  if (previewMode || devMock) {
    return loadFixtureCustomer(customerId);
  }

  // W24 (2026-10-20): url-bootstrap sessions carry a placeholder token
  // the api-server /v1/contacts/:id endpoint cannot verify as a service
  // bearer (that path is not session-gated at the gateway). Rather than
  // show ACCESS DENIED to the rep who legitimately opened this contact
  // in HubSpot, synthesize a minimal authorized customer so the review
  // screens can mount and pull their own scoped data via session-gated
  // routes (/api/customers/*, /v1/audit/query, etc.) which the W24
  // url-bootstrap bypass on the api-server verifier does accept.
  //
  // No sensitive data is fabricated here: the customer id came from the
  // URL the HubSpot card put on this iframe, and the rep is the one
  // viewing that record. The surfaced name is a label built from the id
  // and will be replaced by the first real history fetch.
  const isUrlBootstrap =
    session?.session?.urlBootstrapped === true &&
    typeof customerId === 'string' &&
    customerId.length > 0 &&
    session?.session?.context?.objectId === customerId;
  if (isUrlBootstrap) {
    return {
      status: 'ready',
      customer: {
        id: customerId,
        initials: customerId.slice(0, 2).toUpperCase(),
        firstName: '',
        fullName: `Contact ${customerId}`,
        email: '',
      },
    };
  }

  return loaderResult;
}

// Test-only exports.
export const __test__ = {
  loadFixtureCustomer,
  toAuthorizedCustomer,
  fetchCustomer,
};
